import {
  BarChart,
  Brain,
  Check,
  Code,
  Database,
  Globe,
  HardDrive,
  Layers,
  Server,
  Star,
  Users,
} from "lucide-react";
import { ServiceTemplateCardProps } from "../services/service-creation/service-creation-types";
import { Badge } from "../ui/badge";
import { Card, CardDescription, CardTitle } from "../ui/card";

const categoryIconMap: Record<string, React.ComponentType<any>> = {
  "web-applications": Globe,
  databases: Database,
  development: Code,
  infrastructure: Server,
  "ai-ml": Brain,
  applications: Layers,
  devops: Layers,
  collaboration: Users,
  analytics: BarChart,
  "object-storage": HardDrive,
  "version-control": Layers,
};

const categoryColorMap: Record<string, string> = {
  databases: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  cache:
    "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
  search:
    "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
  "object-storage":
    "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
  "web-applications":
    "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400",
  development:
    "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  "ai-ml": "bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400",
  analytics: "bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400",
  devops: "bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400",
  infrastructure:
    "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

function getCategoryColor(category: string): string {
  return categoryColorMap[category] || "bg-muted text-muted-foreground";
}

function getCategoryIcon(category: string): React.ComponentType<any> {
  return categoryIconMap[category] || Server;
}

export function ServiceTemplateCard({
  template,
  onSelect,
  selected = false,
}: ServiceTemplateCardProps & { selected?: boolean }) {
  const displayName = template.displayName || template.name;
  const isComingSoon = !!template.isComingSoon;
  const isPopular = template.isPopular;
  const tags: string[] = template.tags || [];
  const IconComponent = getCategoryIcon(template.category);
  return (
    <Card
      onClick={isComingSoon ? undefined : () => onSelect(template)}
      className={[
        "relative overflow-hidden transition-all duration-200 group",
        isComingSoon
          ? "cursor-not-allowed opacity-55"
          : selected
            ? "cursor-pointer ring-2 ring-primary ring-offset-1 ring-offset-background"
            : "cursor-pointer card-interactive hover:bg-accent",
      ].join(" ")}
    >
      {/* Coming-soon corner ribbon */}
      {isComingSoon && (
        <div className="absolute top-0 right-0 px-2.5 py-1 bg-muted text-muted-foreground text-2xs font-semibold uppercase tracking-wide rounded-bl-lg z-10">
          Coming Soon
        </div>
      )}
      <div className="p-4 flex items-start gap-3">
        {/* Category-colored icon */}
        <div
          className={`shrink-0 h-11 w-11 rounded-xl flex items-center justify-center transition-colors ${
            selected
              ? "bg-primary text-primary-foreground"
              : getCategoryColor(template.category)
          }`}
        >
          <IconComponent className="h-5 w-5" />
        </div>
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <CardTitle className="text-sm font-semibold leading-snug flex items-center gap-1">
              {displayName}
              {isPopular && !isComingSoon && (
                <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />
              )}
            </CardTitle>
            {!isComingSoon && isPopular && (
              <Badge
                variant="secondary"
                className="shrink-0 text-2xs px-1.5 py-0 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-0"
              >
                Popular
              </Badge>
            )}
            {!isComingSoon && !isPopular && template.version && (
              <span className="shrink-0 text-2xs text-muted-foreground mt-0.5">
                {template.version}
              </span>
            )}
          </div>
          <CardDescription className="text-xs line-clamp-2 leading-relaxed">
            {template.description}
          </CardDescription>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {tags.slice(0, 3).map((tag: string) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="text-2xs px-1.5 py-0 h-4"
                >
                  {tag}
                </Badge>
              ))}
              {tags.length > 3 && (
                <Badge variant="outline" className="text-2xs px-1.5 py-0 h-4">
                  +{tags.length - 3}
                </Badge>
              )}
            </div>
          )}
        </div>
        {/* Selected check */}
        {selected && (
          <div className="shrink-0 h-5 w-5 rounded-full bg-primary flex items-center justify-center">
            <Check className="h-3 w-3 text-primary-foreground" />
          </div>
        )}
      </div>
    </Card>
  );
}
